/**
 * Anti-saccades — inhibitory control of the reflexive gaze shift.
 *
 * A dot appears to one side. The patient must NOT look at it. They look to the
 * mirror-image position on the opposite side of the fixation cross, then come
 * back to the cross. Nothing is drawn at the place they look, and nothing is
 * clicked — the location has to be generated rather than acquired, which is the
 * whole task.
 *
 * THE PATIENT DOES NOTHING WITH THEIR HANDS. See "why there is no response"
 * below; it is the single most important thing about this file.
 *
 * WHY THIS IS A SEPARATE MODULE AND NOT A SACCADES PARAMETER
 * ----------------------------------------------------------
 * docs/ADDING_AN_EXERCISE.md sets the test: a new module is justified when the
 * TASK LOGIC differs — what the patient is asked to do — not when the geometry
 * differs. This clears it comfortably.
 *
 *   Saccades       a target jumps and waits; the patient looks AT it. The
 *                  reflex is the answer.
 *   Anti-saccades  a cue appears and the patient must suppress the movement it
 *                  provokes, then generate the opposite one. The reflex is the
 *                  error.
 *
 * Different instruction, different failure mode. Inverting the response
 * requirement is not a setting on a task whose every parameter assumes the
 * patient is chasing the target.
 *
 * WHY THERE IS NO RESPONSE — decided 2026-08-23
 * ----------------------------------------------
 * An earlier version had the patient click the opposite side, scored by
 * direction, feeding the §9 Layer 3 decay monitor. That is gone, along with the
 * equivalent in all three other oculomotor exercises. The reasoning, in full in
 * DECISIONS.md:
 *
 *   A hand response changes what the patient's EYES are doing. Moving a cursor
 *   to a remembered location — quickly, possibly in tandem stance — pulls
 *   attention onto the hand and imposes a reaction-time task on top of a gaze
 *   task. The number it produced was a visuomotor measure wearing an
 *   oculomotor label, and collecting it degraded the therapy it was measuring.
 *
 * That leaves the exercise unverifiable, and that is accepted: the platform
 * trains and tracks, it does not assess (§11). What carries the record instead
 * is compliance and the symptom rating the patient already taps between
 * exercises.
 *
 * ONE CONSEQUENCE WORTH KNOWING. With pursuit and saccades the stimulus itself
 * elicits the correct movement — a patient looking at the screen tracks the dot
 * by default, so doing it badly is still doing it. Here the reflex points the
 * WRONG way: failing takes no decision and succeeding takes one. The patient
 * instruction below is therefore the only thing holding the task in place, and
 * is written to carry that weight. Confirming the patient has understood it is
 * a conversation in clinic, not something this file can solve.
 */
import { degToPx } from '../types.js';
// =============================================================================
// Clinical colour choices — the same palettes the doctor already knows.
// =============================================================================
const TARGET_COLOURS = [
    { value: '#f0f0f0', label: 'White' },
    { value: '#e02020', label: 'Red' },
    { value: '#2f6fe0', label: 'Blue' },
    { value: '#24a148', label: 'Green' },
    { value: '#f07800', label: 'Orange' },
    { value: '#8a3ffc', label: 'Purple' },
    { value: '#000000', label: 'Black' },
];
const BACKGROUND_COLOURS = [
    { value: '#14161a', label: 'Dark (low stimulus)' },
    { value: '#000000', label: 'Black' },
    { value: '#808080', label: 'Grey' },
    { value: '#ffffff', label: 'White' },
    { value: '#b00020', label: 'Red' },
    { value: '#1a3f8f', label: 'Blue' },
    { value: '#e8c400', label: 'Yellow' },
    { value: '#5b2a86', label: 'Purple' },
];
/** Fixation cross size, in degrees. Small — it marks a point, it is not a target. */
const FIXATION_SIZE_DEG = 0.6;
/**
 * THERE ARE NO INTERMEDIATE STATIONS HERE, unlike saccades. Anti-saccades are
 * run at a single eccentricity: the demand is on suppressing a movement, and a
 * small movement barely provokes one.
 */
const CUE_PATTERNS = {
    horizontal: {
        points: [
            { x: -1, y: 0 },
            { x: 1, y: 0 },
        ],
    },
    vertical: {
        points: [
            { x: 0, y: -1 },
            { x: 0, y: 1 },
        ],
    },
    diagonal_up: {
        points: [
            { x: -1, y: -1 }, // lower left <-> upper right
            { x: 1, y: 1 },
        ],
        fitAspect: true,
    },
    diagonal_down: {
        points: [
            { x: -1, y: 1 }, // upper left <-> lower right
            { x: 1, y: -1 },
        ],
        fitAspect: true,
    },
    cross: {
        points: [
            { x: 0, y: 1 },
            { x: 1, y: 0 },
            { x: 0, y: -1 },
            { x: -1, y: 0 },
        ],
    },
    box: {
        points: [
            { x: -1, y: 1 },
            { x: 1, y: 1 },
            { x: 1, y: -1 },
            { x: -1, y: -1 },
        ],
    },
};
export const antiSaccades = {
    definition: {
        id: 'anti_saccades',
        // Version 1 still. The response was removed before this exercise had ever
        // run for a patient or been deployed, so no stored result was measured the
        // old way and there is no methodology change for a chart to mark.
        version: 1,
        name: 'Anti-saccades',
        category: 'oculomotor',
        clinicalDescription: 'Inhibitory control. A cue appears to one side and the patient suppresses ' +
            'the reflexive gaze shift toward it, looking to the mirror position ' +
            'instead before returning to the centre cross. Horizontal, vertical, ' +
            'diagonal, four-point cross and four-corner box are parameter settings. ' +
            'Removing the fixation point shortly before the cue appears raises the ' +
            'difficulty sharply. The patient makes no response — nothing is clicked — ' +
            'so this trains and is not scored.',
        patientInstruction: 'Keep your eyes on the small cross in the middle. A dot will appear to ' +
            'one side. Do NOT look at the dot. Look to the opposite side, about the ' +
            'same distance away, then bring your eyes back to the cross. Move your ' +
            'eyes, not your head. There is nothing to click. If symptoms start to ' +
            'build, press the spacebar to stop — stopping early is the right call.',
        params: {
            pattern: {
                kind: 'enum',
                label: 'Pattern',
                help: 'Where the cue is allowed to appear. It always has an opposite.',
                options: [
                    { value: 'horizontal', label: 'Horizontal' },
                    { value: 'vertical', label: 'Vertical' },
                    { value: 'diagonal_up', label: 'Diagonal — rising' },
                    { value: 'diagonal_down', label: 'Diagonal — falling' },
                    { value: 'cross', label: 'Cross — four points' },
                    { value: 'box', label: 'Box — four corners' },
                ],
                default: 'horizontal',
            },
            amplitudeDeg: {
                kind: 'number',
                label: 'Amplitude',
                help: 'Distance of the cue from centre, in degrees of visual angle. The ' +
                    'patient looks the same distance the other way, so the full excursion ' +
                    'is twice this.',
                min: 2,
                max: 25,
                step: 1,
                default: 13,
                unit: 'deg',
                fromCentre: true,
            },
            durationSec: {
                kind: 'number',
                label: 'Duration',
                help: 'How long the exercise runs. Cues keep coming until the time is up, ' +
                    'so the number of them follows from this, the fixation interval and ' +
                    'the time on cue rather than being set separately.',
                min: 30,
                max: 600,
                step: 30,
                default: 60,
                unit: 's',
                displayUnit: { unit: 'min', factor: 60 },
            },
            intervalSec: {
                kind: 'number',
                label: 'Fixation interval',
                help: 'How long the patient holds the centre cross between cues. This is ' +
                    'the settling time that makes the next movement a genuine response ' +
                    'rather than a continuation of the last one.',
                min: 0.5,
                max: 5,
                step: 0.25,
                default: 1.5,
                unit: 's',
            },
            holdSec: {
                kind: 'number',
                label: 'Time on cue',
                help: 'How long the cue stays before it goes and the cross comes back. ' +
                    'Shorter is more demanding.',
                min: 0.5,
                max: 6,
                step: 0.25,
                default: 1.5,
                unit: 's',
            },
            randomPosition: {
                kind: 'boolean',
                label: 'Unpredictable position',
                help: 'Places each cue at random rather than working round the pattern in ' +
                    'order. On a predictable side the patient can plan the movement ' +
                    'before the cue arrives, which removes most of the inhibitory ' +
                    'demand — so this is normally left on.',
                default: true,
            },
            randomTiming: {
                kind: 'boolean',
                label: 'Unpredictable timing',
                help: 'Varies the fixation interval so the moment of the cue cannot be ' +
                    'anticipated.',
                default: false,
            },
            gapMs: {
                kind: 'number',
                label: 'Fixation gap',
                help: 'Removes the centre cross this long BEFORE the cue appears, and ' +
                    'leaves it off for the rest of the trial. Releasing fixation early ' +
                    'makes the reflexive movement harder to suppress. Zero leaves the ' +
                    'cross on throughout, which is the easier and more usual starting ' +
                    'point.',
                min: 0,
                max: 600,
                step: 50,
                default: 0,
                unit: 'ms',
                advanced: true,
            },
            targetSizeDeg: {
                kind: 'number',
                label: 'Target size',
                help: 'Diameter of the cue in degrees of visual angle.',
                min: 0.5,
                max: 4,
                step: 0.5,
                default: 1,
                unit: 'deg',
            },
            targetColor: {
                kind: 'enum',
                label: 'Target colour',
                help: 'A clinical parameter, not a branding choice. Lower contrast against ' +
                    'the background for photophobic patients.',
                options: TARGET_COLOURS,
                default: '#f0f0f0',
            },
            backgroundColor: {
                kind: 'enum',
                label: 'Background',
                help: 'The exercise canvas. Darker surfaces are lower stimulus for ' +
                    'photophobic patients; white is the brightest.',
                options: BACKGROUND_COLOURS,
                default: '#808080',
            },
            backgroundTexture: {
                kind: 'boolean',
                label: 'Textured background',
                help: 'Adds a faint depth texture behind the cue.',
                default: true,
            },
        },
        /**
         * Completion only. With no patient response there is nothing else this
         * exercise can honestly report — see "why there is no response" above.
         */
        metrics: ['completion_pct'],
        decay: [],
        decayNotMonitored: 'Takes no patient response, so it emits no performance metric for the ' +
            'monitor to read. Asking for one would pull the patient’s attention ' +
            'onto their hand and change what their eyes were doing, which is the ' +
            'therapy. Symptoms and compliance carry the record. Decided 2026-08-23.',
        allowedPositions: ['seated', 'standing', 'tandem'],
        /** Empty: the patient watches. The only control is the host's stop. */
        allowedInputModes: [],
        involvesHeadMovement: false,
        involvesEyesClosed: false,
        /** The parameter the safety system shortens when reducing a dose. */
        durationParam: 'durationSec',
        estimateDurationSec(params) {
            return Math.round(Number(params.durationSec) + 10); // + instruction screen
        },
        validate(params) {
            const messages = [];
            const amplitude = Number(params.amplitudeDeg);
            const targetSize = Number(params.targetSizeDeg);
            // Blocking: a cue whose edge reaches the fixation cross is not a cue to
            // one side, it is a blob over the middle. There is no eccentricity to
            // look away FROM, so the task has nothing to ask.
            if (targetSize / 2 >= amplitude) {
                messages.push({
                    severity: 'blocking',
                    param: 'targetSizeDeg',
                    message: `The cue is too large for this amplitude: it reaches ${(targetSize / 2).toFixed(1)} ` +
                        `degrees from its own centre while sitting only ${amplitude.toFixed(0)} degrees out, ` +
                        'so it would cover the centre cross. Reduce the target size or increase the amplitude.',
                });
            }
            // Blocking: the gap cannot start before the trial does. With no fixation
            // period there is no fixation to release and no anchor for "opposite",
            // and the task is not an anti-saccade task any more.
            const gapSec = Number(params.gapMs) / 1000;
            const intervalSec = Number(params.intervalSec);
            if (gapSec >= intervalSec) {
                messages.push({
                    severity: 'blocking',
                    param: 'gapMs',
                    message: `The fixation gap is ${gapSec.toFixed(2)} seconds but the fixation interval is only ` +
                        `${intervalSec.toFixed(2)} seconds, so the centre cross would never appear. Shorten ` +
                        'the gap or lengthen the fixation interval.',
                });
            }
            return messages;
        },
    },
    // ===========================================================================
    // The run half — browser only. Nothing above this line touches the DOM.
    // ===========================================================================
    run(ctx) {
        const { params, canvas } = ctx;
        const requestedAmplitudeDeg = Number(params.amplitudeDeg);
        const durationSec = Number(params.durationSec);
        const intervalSec = Number(params.intervalSec);
        const holdSec = Number(params.holdSec);
        const gapSec = Number(params.gapMs) / 1000;
        const targetSizeDeg = Number(params.targetSizeDeg);
        const targetColor = String(params.targetColor);
        const backgroundColor = String(params.backgroundColor);
        const textured = params.backgroundTexture === true;
        const randomPosition = params.randomPosition === true;
        const randomTiming = params.randomTiming === true;
        const patternKey = String(params.pattern);
        const patternSpec = CUE_PATTERNS[patternKey] ?? CUE_PATTERNS.horizontal;
        // Unit positions before the canvas is known. resolveGeometry() replaces
        // these once the aspect ratio can be measured.
        let offsets = patternSpec.points;
        const surface = canvas.getContext('2d');
        let amplitudeDeg = requestedAmplitudeDeg;
        let texture = null;
        // Cached canvas box. Never measure the DOM inside the draw loop — see
        // DECISIONS.md 2026-08-20 on why that shows up as visible judder.
        let box = { width: 0, height: 0 };
        let devicePixelScale = 1;
        let resizeObserver = null;
        function measure() {
            const rect = canvas.getBoundingClientRect();
            box = { width: rect.width, height: rect.height };
            devicePixelScale = rect.width > 0 ? canvas.width / rect.width : 1;
        }
        /**
         * Lay the cue positions out against the real canvas, then fit the amplitude
         * so no cue — and no mirror position the patient has to look to — falls off
         * screen. The mirror is the negation of a cue position, so fitting the cues
         * fits both.
         */
        function resolveGeometry() {
            measure();
            const radiusPx = degToPx(targetSizeDeg, ctx.pxPerDegree) / 2;
            const aspect = box.width > 0 ? box.height / box.width : 1;
            const verticalScale = patternSpec.fitAspect ? aspect : 1;
            offsets = patternSpec.points.map((p) => ({ x: p.x, y: p.y * verticalScale }));
            // How far the furthest cue sits on each axis, in amplitude units. An axis
            // nothing travels on imposes no limit.
            const reachX = Math.max(...offsets.map((o) => Math.abs(o.x)));
            const reachY = Math.max(...offsets.map((o) => Math.abs(o.y)));
            const maxAmplitudeFor = (reach, halfPx) => reach > 0 ? Math.max(halfPx - radiusPx, 0) / ctx.pxPerDegree / reach : Infinity;
            amplitudeDeg = Math.min(requestedAmplitudeDeg, maxAmplitudeFor(reachX, box.width / 2), maxAmplitudeFor(reachY, box.height / 2));
            // What this screen can actually show, which is not always what was
            // prescribed. Reported on every resolve, including after a resize, so the
            // record describes the run rather than the intention.
            ctx.reportDelivered({ amplitudeDeg: Math.round(amplitudeDeg * 100) / 100 });
        }
        function buildTexture() {
            if (!surface || !textured)
                return null;
            const tile = document.createElement('canvas');
            tile.width = 64;
            tile.height = 64;
            const tileSurface = tile.getContext('2d');
            if (!tileSurface)
                return null;
            tileSurface.fillStyle = backgroundColor;
            tileSurface.fillRect(0, 0, 64, 64);
            tileSurface.fillStyle = 'rgba(128,128,128,0.14)';
            for (let x = 0; x < 64; x += 8) {
                for (let y = 0; y < 64; y += 8) {
                    const jitterX = ((x * 7 + y * 13) % 5) - 2;
                    const jitterY = ((x * 11 + y * 5) % 5) - 2;
                    tileSurface.beginPath();
                    tileSurface.arc(x + jitterX, y + jitterY, 1.2, 0, Math.PI * 2);
                    tileSurface.fill();
                }
            }
            return surface.createPattern(tile, 'repeat');
        }
        let frame = 0;
        let stopped = false;
        let lastFrameAt = 0;
        let runningSec = 0;
        let cuesPresented = 0;
        /** Guards against counting the same cue on every frame it is drawn. */
        let countedThisCue = false;
        /** Index into `offsets` of the current cue. -1 before the first trial. */
        let cueIndex = -1;
        /** When the current cue appears, and when its trial ends, in running seconds. */
        let cueAtSec = 0;
        let trialEndsAtSec = 0;
        function nextCueIndex() {
            const count = offsets.length;
            if (randomPosition) {
                // Never repeat the same side twice running — a repeat is a trial with
                // no movement to suppress, and on a two-point pattern it would also
                // make the sequence predictable in the one way this setting exists to
                // prevent.
                if (count <= 1)
                    return 0;
                let next = cueIndex;
                while (next === cueIndex)
                    next = Math.floor(Math.random() * count);
                return next;
            }
            return (cueIndex + 1) % count;
        }
        function startTrial() {
            cueIndex = nextCueIndex();
            countedThisCue = false;
            const jitter = randomTiming ? Math.random() * intervalSec : 0;
            cueAtSec = runningSec + intervalSec + jitter;
            trialEndsAtSec = cueAtSec + holdSec;
        }
        function cueIsUp() {
            return runningSec >= cueAtSec;
        }
        /**
         * Zero gap leaves the cross on for the whole trial — the overlap condition,
         * where fixation supports the suppression. Any gap takes it off early and
         * leaves it off, which is the harder gap condition.
         */
        function fixationVisible() {
            if (gapSec <= 0)
                return true;
            return runningSec < cueAtSec - gapSec;
        }
        function cuePx() {
            const offset = offsets[cueIndex] ?? offsets[0];
            return {
                x: box.width / 2 + degToPx(offset.x * amplitudeDeg, ctx.pxPerDegree),
                y: box.height / 2 - degToPx(offset.y * amplitudeDeg, ctx.pxPerDegree),
            };
        }
        /**
         * Completion is the share of the prescribed TIME that was actually run —
         * the same meaning it carries on every other exercise, which is what lets
         * one chart hold all of them. It is the only metric this exercise emits.
         */
        function emitCompletion() {
            ctx.emit({
                t: runningSec * 1000,
                metric: 'completion_pct',
                value: Math.min(100, (runningSec / durationSec) * 100),
                detail: { cuesPresented },
            });
        }
        function finishRun(reason) {
            emitCompletion();
            ctx.finish(reason);
        }
        function draw() {
            if (stopped || !surface)
                return;
            const nowMs = ctx.now();
            const deltaSec = Math.min((nowMs - lastFrameAt) / 1000, 0.1);
            lastFrameAt = nowMs;
            runningSec += deltaSec;
            // --- advance the trial ------------------------------------------------
            if (runningSec >= trialEndsAtSec) {
                if (runningSec >= durationSec) {
                    finishRun('completed');
                    return;
                }
                startTrial();
            }
            // The clock can also run out mid-trial. End on time rather than waiting
            // for the current one — a prescribed minute must be a minute.
            if (runningSec >= durationSec) {
                finishRun('completed');
                return;
            }
            // --- render -----------------------------------------------------------
            surface.setTransform(devicePixelScale, 0, 0, devicePixelScale, 0, 0);
            surface.fillStyle = backgroundColor;
            surface.fillRect(0, 0, box.width, box.height);
            if (texture) {
                surface.fillStyle = texture;
                surface.fillRect(0, 0, box.width, box.height);
            }
            // The fixation mark is a CROSS and the cue is a filled circle,
            // deliberately different shapes. A patient who confuses the two is
            // running a different exercise, and with no response nothing in the
            // record would ever reveal it.
            if (fixationVisible()) {
                const arm = Math.max(degToPx(FIXATION_SIZE_DEG, ctx.pxPerDegree) / 2, 2);
                surface.strokeStyle = targetColor;
                surface.lineWidth = Math.max(arm / 4, 1);
                surface.beginPath();
                surface.moveTo(box.width / 2 - arm, box.height / 2);
                surface.lineTo(box.width / 2 + arm, box.height / 2);
                surface.moveTo(box.width / 2, box.height / 2 - arm);
                surface.lineTo(box.width / 2, box.height / 2 + arm);
                surface.stroke();
            }
            if (cueIsUp()) {
                const cue = cuePx();
                const radiusPx = Math.max(degToPx(targetSizeDeg, ctx.pxPerDegree) / 2, 1);
                surface.beginPath();
                surface.arc(cue.x, cue.y, radiusPx, 0, Math.PI * 2);
                surface.fillStyle = targetColor;
                surface.fill();
                if (!countedThisCue) {
                    cuesPresented += 1;
                    countedThisCue = true;
                }
            }
            frame = requestAnimationFrame(draw);
        }
        return {
            start() {
                resolveGeometry();
                texture = buildTexture();
                lastFrameAt = ctx.now();
                if (typeof ResizeObserver !== 'undefined') {
                    resizeObserver = new ResizeObserver(() => measure());
                    resizeObserver.observe(canvas);
                }
                startTrial();
                frame = requestAnimationFrame(draw);
            },
            /** Instant and idempotent (rule 5). May be called twice. */
            stop(reason) {
                if (stopped)
                    return;
                stopped = true;
                cancelAnimationFrame(frame);
                resizeObserver?.disconnect();
                resizeObserver = null;
                if (reason !== 'completed')
                    emitCompletion();
            },
        };
    },
};
//# sourceMappingURL=anti-saccades.js.map